import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, type BlockKind, type BlockRules } from "../api";
import { EmptyState } from "../components/Feedback";
import { formatDateTime } from "../utils";

interface BlocksScreenProps {
  showSuccess: (message: string) => void;
  showError: (error: unknown) => void;
}

type BlockTab = "videos" | "channels" | "programs" | "keywords";

const EMPTY_RULES: BlockRules = { videos: [], channels: [], programs: [], keywords: [] };

export function BlocksScreen({ showSuccess, showError }: BlocksScreenProps) {
  const [rules, setRules] = useState<BlockRules>(EMPTY_RULES);
  const [tab, setTab] = useState<BlockTab>("videos");
  const [value, setValue] = useState("");
  const [matchType, setMatchType] = useState<"EXACT" | "CONTAINS">("CONTAINS");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRules(await api.blocks());
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { void load(); }, [load]);

  async function addRule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const clean = value.trim();
    if (clean.length < 2) return;
    setBusy(true);
    try {
      if (tab === "videos") await api.blockVideo(clean, "Added from block rules");
      else if (tab === "channels") await api.blockChannel(clean, "", "Added from block rules");
      else if (tab === "programs") await api.blockProgram(clean);
      else await api.blockKeyword(clean, matchType);
      setValue("");
      await load();
      showSuccess("Đã thêm block rule. Áp dụng cho cả hai TV.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  }

  async function removeRule(kind: BlockKind, id: string) {
    setBusy(true);
    try {
      await api.removeBlock(kind, id);
      await load();
      showSuccess("Đã gỡ block rule.");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  }

  const total = rules.videos.length + rules.channels.length + rules.programs.length + rules.keywords.length;
  const placeholders: Record<BlockTab, string> = {
    videos: "YouTube Video ID hoặc URL",
    channels: "YouTube Channel ID (bắt đầu bằng UC…)",
    programs: "Ví dụ: Skibidi Toilet",
    keywords: "Ví dụ: prank, horror, scary",
  };

  return (
    <div className="page-stack">
      <header className="page-header">
        <div><p className="eyebrow">CONTENT SAFETY</p><h1>Nội dung bị chặn</h1><p>Mọi rule được áp dụng tại backend trước khi nội dung đến Samsung hoặc Android TV.</p></div>
        <span className="count-badge">{total} rule</span>
      </header>

      <div className="segmented-tabs block-tabs" role="tablist">
        {([
          ["videos", "Video", rules.videos.length],
          ["channels", "Kênh", rules.channels.length],
          ["programs", "Chương trình", rules.programs.length],
          ["keywords", "Từ khóa", rules.keywords.length],
        ] as const).map(([key, label, count]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} className={tab === key ? "active" : ""} onClick={() => { setTab(key); setValue(""); }}>
            {label}<span>{count}</span>
          </button>
        ))}
      </div>

      <form className="block-rule-form" onSubmit={addRule}>
        <label>
          {tab === "videos" ? "Chặn video" : tab === "channels" ? "Chặn kênh" : tab === "programs" ? "Chặn chương trình" : "Chặn từ khóa"}
          <input value={value} onChange={(event) => setValue(event.target.value)} placeholder={placeholders[tab]} minLength={2} maxLength={500} required />
        </label>
        {tab === "keywords" && (
          <label>
            Kiểu so khớp
            <select value={matchType} onChange={(event) => setMatchType(event.target.value as "EXACT" | "CONTAINS")}>
              <option value="CONTAINS">CONTAINS – truy vấn hoặc tiêu đề có chứa</option>
              <option value="EXACT">EXACT – truy vấn hoặc tiêu đề trùng hoàn toàn</option>
            </select>
          </label>
        )}
        <button type="submit" className="primary-button" disabled={busy || value.trim().length < 2}>+ Thêm rule</button>
      </form>

      <div className="safety-note"><span aria-hidden="true">i</span><p>Rule từ khóa được normalize và áp dụng cho cả truy vấn trẻ nhập lẫn tiêu đề video. Truy vấn bị chặn trả về 0 kết quả, không gọi YouTube.</p></div>

      {loading ? <div className="skeleton skeleton-section" /> : (
        <div className="rule-list">
          {tab === "videos" && rules.videos.map((rule) => (
            <article className="rule-row" key={rule.youtube_video_id}>
              <span className="rule-icon">▶</span><div><strong>{rule.title_snapshot ?? rule.youtube_video_id}</strong><small>{rule.youtube_video_id} · {formatDateTime(rule.created_at)}</small></div>
              <button type="button" className="danger-text" onClick={() => void removeRule("videos", rule.youtube_video_id)} disabled={busy}>Gỡ chặn</button>
            </article>
          ))}
          {tab === "channels" && rules.channels.map((rule) => (
            <article className="rule-row" key={rule.youtube_channel_id}>
              <span className="rule-icon">▣</span><div><strong>{rule.channel_name_snapshot || "YouTube Channel"}</strong><small>{rule.youtube_channel_id} · {formatDateTime(rule.created_at)}</small></div>
              <button type="button" className="danger-text" onClick={() => void removeRule("channels", rule.youtube_channel_id)} disabled={busy}>Gỡ chặn</button>
            </article>
          ))}
          {tab === "programs" && rules.programs.map((rule) => (
            <article className="rule-row" key={rule.id}>
              <span className="rule-icon">Aa</span><div><strong>{rule.value}</strong><small>So khớp tên chương trình · {formatDateTime(rule.created_at)}</small></div>
              <button type="button" className="danger-text" onClick={() => void removeRule("programs", rule.id)} disabled={busy}>Xóa</button>
            </article>
          ))}
          {tab === "keywords" && rules.keywords.map((rule) => (
            <article className="rule-row" key={rule.id}>
              <span className="rule-icon">#</span><div><strong>{rule.value}</strong><small>{rule.match_type} · {formatDateTime(rule.created_at)}</small></div>
              <button type="button" className="danger-text" onClick={() => void removeRule("keywords", rule.id)} disabled={busy}>Xóa</button>
            </article>
          ))}
          {rules[tab].length === 0 && <EmptyState icon="✓" title="Chưa có rule loại này" description="Nội dung Shorts vẫn luôn bị chặn bởi chính sách hệ thống, không phụ thuộc danh sách này." />}
        </div>
      )}
    </div>
  );
}
